<script>
import {
  iDrawer,
  iAlertMessage,
  iAlertMessageType,
  iAlertMessageSize,
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
  iIcon,
} from '@inscribe/signal';
import {
  CUSTOMER_SIGNAL,
  CUSTOMER_SIGNAL_CONFIG,
} from '@/constants/signals/customer-signals.constants';
import {
  SEVERITY_LEVEL_BY_FRAUD_SIGNAL,
  SEVERITY_LEVEL,
  severityLevelToReadableLabel,
} from '@/constants/signalNames';
import {
  default as StatusBar,
  StatusBarSizes,
} from '@/V2/components/status/StatusBar.vue';
import { mapSeverityToStatus } from '@/V2/documents/document-view/document-review-agent/utils';
import CustomerFraudSignalsDataBlock from '@/V2/customers/fraud/CustomerFraudSignalsDataBlock.vue';
import signalConfig from '@/constants/signalConfig';
import CustomerFraudVerificationDetails from '@/V2/customers/fraud/CustomerFraudVerificationDetails.vue';
import CustomerFraudCrossCheckDetails from '@/V2/customers/fraud/CustomerFraudCrossCheckDetails.vue';
import CustomerFraudSignalTransactionsList from '@/V2/customers/fraud/CustomerFraudSignalTransactionsList.vue';
import CustomerFraudSignalDailyBalancesList from '@/V2/customers/fraud/CustomerFraudSignalDailyBalancesList.vue';

export default {
  components: {
    iDrawer,
    StatusBar,
    iAlertMessage,
    iButton,
    CustomerFraudSignalsDataBlock,
    CustomerFraudVerificationDetails,
    CustomerFraudCrossCheckDetails,
    CustomerFraudSignalTransactionsList,
    CustomerFraudSignalDailyBalancesList,
    iIcon,
  },
  props: {
    iDrawerVisible: {
      type: Boolean,
      required: false,
    },
    selectedSignal: {
      type: Object,
    },
    aggregatedDocumentsResults: {
      type: Object,
    },
  },
  data() {
    return {
      CUSTOMER_SIGNAL,
      CUSTOMER_SIGNAL_CONFIG,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
      iAlertMessageType,
      iAlertMessageSize,
      severityLevelToReadableLabel,
      mapSeverityToStatus,
      signalConfig,
      SEVERITY_LEVEL,
      StatusBarSizes,
    };
  },
  computed: {
    severityOptions() {
      return this.severityLevels.map((severity) => ({
        value: severity,
        text: severityLevelToReadableLabel(severity),
      }));
    },
    selectedSeverity() {
      return this.severityLevels.find(
        (severity) => severity === this.currentSeverityValue,
      );
    },
    isCustomersSignal() {
      return (
        this.selectedSignal?.key ===
        CUSTOMER_SIGNAL.PREVIOUS_HIGH_RISK_APPLICATIONS
      );
    },
    groupedSignals() {
      let groupedSignals = {};
      Object.keys(this.aggregatedDocumentsResults).forEach((key) => {
        const severity = SEVERITY_LEVEL_BY_FRAUD_SIGNAL[key];
        if (!groupedSignals[severity]) {
          groupedSignals[severity] = [];
        }
        const signalObject = signalConfig[key];
        groupedSignals[severity].push({
          ...signalObject,
          title: signalObject?.title || key,
          documents: this.aggregatedDocumentsResults[key]?.details?.documents,
        });
      });
      return groupedSignals;
    },
    signalTitle() {
      return (
        this.selectedSignal?.label ||
        CUSTOMER_SIGNAL_CONFIG[this.selectedSignal?.key]?.label
      );
    },
    hasTransactions() {
      return this.selectedSignal?.transactions?.length > 0;
    },
    hasDailyBalances() {
      return this.selectedSignal?.daily_balances?.length > 0;
    },
    viewAllButtonLabel() {
      return this.selectedSignal.isConfigurable
        ? 'View All Transactions'
        : this.isCustomersSignal
          ? 'View All Customers'
          : 'View All Documents';
    },
  },
  methods: {
    hideDrawer() {
      this.$emit('hide-drawer');
    },
    getDetailTitle(typeKey) {
      return signalConfig[typeKey.toLowerCase()]?.title || typeKey;
    },
    getSignalDescription(typeKey) {
      return signalConfig[typeKey.toLowerCase()]?.description || typeKey;
    },
    viewAllDocuments() {
      this.$emit('hide-drawer');
      this.$nextTick(() => {
        this.$router
          .push({
            name: 'customer-documents',
            params: {
              customerId: this.customerId,
            },
          })
          .catch((err) => {});
      });
    },
    viewAllCustomers() {
      this.$emit('hide-drawer');
      this.$nextTick(() => {
        this.$router.push({ name: 'customers-view' });
      });
    },
    viewAllTransactions() {
      this.$emit('hide-drawer');
      this.$router.push({
        name: 'customer-credit-transactions',
        params: {
          customer_id: this.$route.params.customer_id,
        },
      });
    },
    viewAllButtonClick() {
      if (this.selectedSignal.isConfigurable) {
        this.viewAllTransactions();
      } else if (this.isCustomersSignal) {
        this.viewAllCustomers();
      } else {
        this.viewAllDocuments();
      }
    },
  },
};
</script>

<template>
  <i-drawer
    :visible="iDrawerVisible"
    class="multiple-severity-signals"
    :title="signalTitle"
    @hide-drawer="hideDrawer"
  >
    <div
      id="collect-session-detail"
      class="d-flex flex-column i-gap-9"
      data-testid="customer-fraud-signal-details"
    >
      <status-bar
        v-if="selectedSignal?.highRisk === true"
        :state="mapSeverityToStatus(SEVERITY_LEVEL.HIGH)"
        :label="severityLevelToReadableLabel(SEVERITY_LEVEL.HIGH)"
        :size="StatusBarSizes.XLARGE"
      />

      <!-- configurable signal trigger details -->
      <i-alert-message
        v-if="selectedSignal.isConfigurable"
        class="trigger-details-alert"
        :type="iAlertMessageType.NEUTRAL"
        :size="iAlertMessageSize.MEDIUM"
        :heading="
          selectedSignal?.impacts_risk
            ? 'Why it triggered'
            : 'Does not affect customer risk rating'
        "
        :message="selectedSignal?.rationale"
      ></i-alert-message>

      <!-- type MULTIPLE_HIGH_RISK_DOCUMENTS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.MULTIPLE_HIGH_RISK_DOCUMENTS
        "
      >
        <customer-fraud-signals-data-block
          title="High-Risk Documents"
          :data="selectedSignal?.value?.details?.documents"
          type="documents"
        />
      </div>

      <!-- type UNUSUAL_TRANSACTION_PATTERNS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.UNUSUAL_TRANSACTION_PATTERNS
        "
      >
        <div class="d-flex flex-column i-gap-9">
          <customer-fraud-signals-data-block
            v-for="typeKey in Object.keys(selectedSignal?.value?.details || {})"
            :key="typeKey"
            :title="getDetailTitle(typeKey)"
            :description="getSignalDescription(typeKey)"
            :data="selectedSignal?.value?.details[typeKey]?.documents"
          />
        </div>
      </div>

      <!-- type MULTIPLE_HIGH_RISK_DOCUMENTS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.MULTIPLE_DOCUMENT_RISK_SIGNALS
        "
      >
        <div class="d-flex flex-column i-gap-9">
          <customer-fraud-signals-data-block
            v-for="signal in groupedSignals[SEVERITY_LEVEL.HIGH]"
            :key="signal.name"
            :title="signal.title"
            :description="signal.tooltipText"
            :data="signal.documents"
          />
        </div>
      </div>

      <!-- type PREVIOUS_HIGH_RISK_APPLICATIONS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.PREVIOUS_HIGH_RISK_APPLICATIONS
        "
      >
        <customer-fraud-signals-data-block
          title="Previous Applications"
          description="Previous Applications"
          :data="selectedSignal?.value?.details?.customers"
          type="customers"
        />
      </div>

      <!-- verifications -->
      <div v-if="selectedSignal.isVerification">
        <customer-fraud-verification-details
          :signal="selectedSignal"
        ></customer-fraud-verification-details>
      </div>

      <!-- salary cross check -->
      <div
        class="salary-cross-check"
        v-if="selectedSignal.key === CUSTOMER_SIGNAL.SALARY_CROSS_CHECK"
      >
        <customer-fraud-cross-check-details
          :signal="selectedSignal"
        ></customer-fraud-cross-check-details>
      </div>

      <!-- configurable signal details -->
      <div v-if="selectedSignal.isConfigurable">
        <customer-fraud-signal-transactions-list
          v-if="hasTransactions"
          :transactions="selectedSignal?.transactions"
        ></customer-fraud-signal-transactions-list>
        <customer-fraud-signal-daily-balances-list
          v-if="hasDailyBalances"
          :daily-balances="selectedSignal?.daily_balances"
        ></customer-fraud-signal-daily-balances-list>
      </div>
    </div>

    <template #footer>
      <i-button
        :label="viewAllButtonLabel"
        class="view-all-button"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.LARGE"
        @button-click="viewAllButtonClick()"
      />
    </template>
  </i-drawer>
</template>

<style lang="scss">
.multiple-severity-signals {
  .trigger-details {
    background-color: var(--fill-neutral-subtle);
    .title {
      color: var(--text-neutral-bold);
    }
    .description {
      color: var(--text-neutral-bold);
    }
  }

  .view-all-button {
    width: 100%;
  }
}
</style>
